// src/components/RoomList.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Grid,
  CircularProgress,
  CardMedia,
} from "@mui/material";
import getRoom from "../api/app/getRoom";
import getRoomImage from "../api/app/getRoomImage";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import TimeBar from "./TimeBar";
import getSimulatorReservations from "../api/app/getSimulatorReservations";
import { RESERVATION_TIME } from "../data/RESERVATION_TIME";
// ---------- utils ----------
const formatDate = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// Parse ISO string as local time (no UTC conversion)
const parseRawDateTime = (dateTimeStr) => {
  const match = dateTimeStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):?(\d{2})?/);
  if (match) {
    const [, year, month, day, hour, minute, second = "0"] = match;
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second)
    );
  }
  return new Date(dateTimeStr);
};

const pickImageUID = (room) => {
  const arr = Array.isArray(room?.bannerImages) ? room.bannerImages : [];
  return arr.length > 0 ? arr[0] : null;
};



// ---------- styled ----------
const GlowTitle = styled(Typography)({
  color: "#fff",
  fontWeight: 700,
  fontSize: 20,
  textShadow: "0 0 6px rgba(0,0,0,0.6)",
});

const SubInfo = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  fontSize: 15,
}));

// ---------- component ----------
/**
 * Props:
 * - token: string
 * - storeId: string
 * - step: number  // use 2 to fetch tables
 * - onSelectRoom: (room) => void
 * - getSimulatorReservations?: (simId: string, dayStr: string) => Promise<Array<{startTime,endTime}>>
 */
const RoomList = ({ token, storeId }) => {
  const [tableData, setTableData] = useState([]);
  const [imgMap, setImgMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [timeBarReservations, setTimeBarReservations] = useState([]);
  const [error, setError] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  // placeholder image
  const placeholder =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='400'>
        <rect width='100%' height='100%' fill='#333'/>
        <text x='50%' y='50%' fill='#888' text-anchor='middle' dominant-baseline='middle' font-size='28'>
        No Image</text></svg>`
    );

  // Step-based data setup
  useEffect(() => {
    if (!token) return;

    const run = async () => {
      try {
        setLoading(true);
        setError(false);

        console.log("[RoomList] Fetching rooms for storeId:", storeId);
        const resp = await getRoom(storeId);
        console.log("[RoomList] getRoom response:", resp);
        if (!resp) throw new Error("no response from getRoom");

        const simulators = (resp.simulators ?? [])
          .filter((sim) => sim.isActive !== false)
          .sort((a, b) =>
            String(a.code || "").localeCompare(String(b.code || ""))
          );
        console.log("[RoomList] Active simulators:", simulators.length);

        // Fill price table and reservations like your snippet
        await Promise.all(
          simulators.map(async (sim) => {
            // Normalize fields
            sim.PriceTable = sim.PriceTable || {
              PlanId: [],
              Hour: [],
              Price: [],
              FreeHour: [],
              Discount: [],
            };
            sim.Reservations = sim.Reservations || []; // existing busy periods or empty
            sim.Name = sim.name;
            sim.SimulatorId = sim.id;

            (sim.plans ?? []).forEach((p) => {
              sim.PriceTable.PlanId.push(p.id);
              sim.PriceTable.Hour.push(parseFloat(p.hours));
              sim.PriceTable.Price.push(p.price);
              sim.PriceTable.FreeHour.push(0);
              sim.PriceTable.Discount.push(0);
            });

            try {
              setLoading(true);
              const responseData = await getSimulatorReservations(
                sim.id,
                formatDate(new Date())
              );
              console.log("sim.id:", sim.id);
              console.log("formatDate(new Date()):", formatDate(new Date()));
              console.log("getSimulatorReservations responseData:", responseData);
              if (responseData !== null) {
                let reservations = [];
                let currentTime = new Date(); // Start of the current day

                responseData.forEach((period) => {
                  const periodStart = parseRawDateTime(period.startTime);
                  const periodEnd = parseRawDateTime(period.endTime);

                  if (currentTime < periodStart) {
                    reservations.push({
                      StartTime: currentTime,
                      EndTime: periodStart,
                    });
                  }
                  currentTime = periodEnd;
                });

                //Add remaining period until the end of the day
                const endOfDay = new Date(new Date().setHours(23, 59, 59, 999));
                if (currentTime < endOfDay) {
                  reservations.push({
                    StartTime: currentTime,
                    EndTime: endOfDay,
                  });
                }

                sim.Reservations = [
                  ...sim.Reservations,
                  ...reservations,
                ];
              }
            } catch (err) {
              console.error(err);
            } finally {
              setLoading(false);
            }
          })
        );

        setTableData(simulators);
      } catch (e) {
        console.error(e);
        setError(true);
        setTableData([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [token, storeId]);

  // Load cover images for each room
  useEffect(() => {
    let cancelled = false;

    const loadImages = async () => {
      const entries = await Promise.all(
        tableData.map(async (room) => {
          const uid = pickImageUID(room);
          if (!uid) return [room.id, null];
          const url = await getRoomImage(uid); // arraybuffer->blob URL inside util
          return [room.id, url];
        })
      );
      if (!cancelled) {
        const map = {};
        for (const [id, url] of entries) map[id] = url;
        setImgMap(map);
      }
    };

    if (tableData.length) loadImages();
    return () => {
      cancelled = true;
    };
  }, [tableData]);

  if (!token) {
    return (
      <Box p={8} textAlign="center">
        <SubInfo color="text.secondary">請先登入以查看包廂資訊</SubInfo>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box pt={20} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={10} textAlign="center">
        <SubInfo color="error">無法取得包廂列表</SubInfo>
      </Box>
    );
  }

  const handleClick = (room) => {
    navigate(`/app/${storeId}/reserve`, {
      state: {
        tables: [room],
        store: {
          Id: storeId,
          Promotions: [],
          IntervalMinutes: 30,
          OnlinePaymentAvailable: true,
        },
      },
    });
  };
  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {tableData.map((room) => {
          const imgUrl = imgMap[room.id] || placeholder;

          return (
            <Grid key={room.id} item xs={12} sm={6} md={4}>
              <Card
                onClick={() => handleClick(room)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  overflow: "hidden",
                  boxShadow: "0 3px 12px rgba(0,0,0,0.15)",
                  transition: "transform 0.25s, box-shadow 0.25s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
                  },
                }}
              >
                {/* header image */}
                <Box sx={{ position: "relative", height: 200 }}>
                  <CardMedia
                    component="img"
                    image={imgUrl}
                    alt={room.name}
                    sx={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                      filter: "brightness(0.95)",
                    }}
                    onError={(e) => (e.currentTarget.src = placeholder)}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      width: "100%",
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                      p: 1.5,
                    }}
                  >
                    <GlowTitle>{room.name}</GlowTitle>
                  </Box>
                </Box>

                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minHeight: 40, lineHeight: 1.4 }}
                  >
                    {room.introduce || "—"}
                  </Typography>



                  <Box mt={2}>
                    <TimeBar reservations={room.Reservations} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default RoomList;
